<template>
  <div>
    <Nav />
    <!-- 主要内容区 -->
    <div class="flex pt-20 max-w-7xl mx-auto">
      <LeftNav activeItem="favorites" />
      <main class="flex-1 p-6">
        <div class="max-w-5xl">
          <div class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900">收藏夹</h1>
            <p class="text-gray-600 mt-1">查看收藏的会展设备</p>
          </div>
          <div class="mb-6">
            <div class="flex items-center justify-between">
              <div class="text-gray-600">
                共
                <span class="text-gray-900 font-medium">{{ totalCount }}</span>
                件收藏设备
              </div>
              <div class="flex items-center space-x-4">
                <div class="relative">
                  <button
                    id="sortBtn"
                    class="flex items-center space-x-2 bg-white border border-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
                    @click="toggleSortDropdown"
                  >
                    <span id="sortText">{{ sortText }}</span>
                    <div class="w-4 h-4 flex items-center justify-center">
                      <i class="ri-arrow-down-s-line text-gray-500"></i>
                    </div>
                  </button>
                  <div
                    id="sortDropdown"
                    class="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-32"
                    :class="{ hidden: !showSortDropdown }"
                  >
                    <div class="py-1">
                      <button
                        v-for="option in sortOptions"
                        :key="option.key"
                        class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                        :class="{
                          'bg-blue-50 text-primary': sortBy === option.key,
                        }"
                        @click="handleSortChange(option.key)"
                      >
                        {{ option.label }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 加载状态 -->
          <div v-if="loading" class="flex justify-center items-center py-20">
            <div class="text-primary text-lg font-medium">
              加载收藏列表中...
            </div>
          </div>

          <!-- 错误状态 -->
          <div
            v-else-if="error"
            class="flex flex-col justify-center items-center py-20"
          >
            <i class="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
            <div class="text-gray-700 mb-4">{{ error }}</div>
            <button
              class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              @click="loadFavoriteList"
            >
              重试
            </button>
          </div>

          <!-- 空状态 -->
          <div
            v-else-if="favoriteList.length === 0"
            class="flex flex-col justify-center items-center py-20"
          >
            <i class="ri-heart-line text-4xl text-gray-300 mb-4"></i>
            <div class="text-gray-500 mb-4">暂无收藏的设备</div>
            <button
              class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              @click="goToRentPage"
            >
              去浏览设备
            </button>
          </div>

          <!-- 收藏设备列表 -->
          <div v-else class="space-y-4">
            <div
              v-for="item in favoriteList"
              :key="item.id"
              class="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
              :data-product-id="item.id"
            >
              <div class="flex items-center space-x-6">
                <div
                  class="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0"
                >
                  <img
                    :src="item.thumbnail_url || '/img/default-product.jpg'"
                    :alt="item.name"
                    class="w-full h-full object-cover object-top"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-medium text-gray-900 mb-2" @click="goToDetail(item.goods_id)">
                    {{ item.name }}
                  </h3>
                  <div
                    class="flex items-center space-x-4 text-sm text-gray-500 mb-3"
                  >
                    <span>收藏时间：{{ formatDate(item.createtime) }}</span>
                    <span>•</span>
                    <span>{{ item.supplier_name || "未知供应商" }}</span>
                  </div>
                  <div class="text-2xl font-bold text-primary">
                    ¥{{ formatPrice(item.price)
                    }}<span class="text-sm font-normal text-gray-500">/天</span>
                  </div>
                </div>
                <div class="flex items-center space-x-3">
                  <button
                    class="unfavorite-btn border border-gray-300 text-gray-700 px-4 py-2 !rounded-button hover:bg-gray-50 transition-colors whitespace-nowrap text-sm"
                    @click="handleRemoveFavorite(item.id)"
                  >
                    <div
                      class="w-4 h-4 flex items-center justify-center mr-1 float-left"
                    >
                      <i class="ri-heart-fill text-red-500"></i>
                    </div>
                    取消收藏
                  </button>
                  <button
                    class="add-to-cart-btn bg-orange-500 text-white px-4 py-2 !rounded-button hover:bg-orange-600 transition-colors whitespace-nowrap text-sm"
                    @click="handleAddToCart(item.id)"
                  >
                    <div
                      class="w-4 h-4 flex items-center justify-center mr-1 float-left"
                    >
                      <i class="ri-shopping-cart-line"></i>
                    </div>
                    加入购物车
                  </button>
                  <button
                    class="rent-now-btn bg-primary text-white px-4 py-2 !rounded-button hover:bg-secondary transition-colors whitespace-nowrap text-sm"
                    @click="handleQuickRent(item.id)"
                  >
                    立即租赁
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 分页控件 -->
          <div>
            <Pagination
              :totalCount="totalCount"
              :page_current="page_current"
              :page_size="page_size"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </main>
    </div>
    <!-- 页脚 -->
    <Footer />
  </div>
</template>

<script>
import { Message } from "element-ui";
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";
import LeftNav from "@/components/LeftNav.vue";
import Pagination from "@/components/Pagination.vue";
import api from "@/utils/api";
import { formatDate, formatPrice } from "@/utils/tool.js";

export default {
  name: "Favorites",
  components: {
    Nav,
    Footer,
    LeftNav,
    Pagination,
  },
  data() {
    return {
      // 收藏列表数据
      favoriteList: [],
      // 用户信息
      userInfo: {},
      userName: "",
      userEmail: "",
      userAvatar: "",
      // 分页相关
      page_current: 1,
      page_size: 10,
      totalCount: 0,
      // 加载状态
      loading: false,
      // 错误信息
      error: "",
      // 排序相关
      sortBy: "time",
      showSortDropdown: false,
      // 排序选项配置
      sortOptions: [
        { key: "time", label: "收藏时间" },
        { key: "price-low", label: "价格升序" },
        { key: "price-high", label: "价格降序" },
      ],
    };
  },
  computed: {
    // 排序文本
    sortText() {
      switch (this.sortBy) {
        case "time":
          return "收藏时间";
        case "price-low":
          return "价格升序";
        case "price-high":
          return "价格降序";
        default:
          return "收藏时间";
      }
    },
  },
  mounted() {
    // 加载收藏列表
    this.loadFavoriteList();
    // 添加点击外部关闭下拉菜单的事件监听
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeDestroy() {
    // 移除事件监听
    document.removeEventListener("click", this.handleDocumentClick);
  },
  methods: {
    // 返回上一页
    goBack() {
      this.$router.back();
    },

    // 加载收藏列表
    async loadFavoriteList() {
      this.loading = true;
      this.error = "";

      try {
        const params = {
          page_current: this.page_current,
          page_size: this.page_size,
          sortBy: this.sortBy,
        };

        const response = await api.user.getFavoriteList(params);

        this.favoriteList = response.data.resdata || [];
        this.totalCount = response.data.restotal || 0;
      } catch (error) {
        console.error("获取收藏列表失败:", error);
        this.error = "网络错误，请检查网络连接后重试";
        this.favoriteList = [];
        this.totalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    // 取消收藏
    async handleRemoveFavorite(id) {
      try {
        const response = await api.user.removeFavorite({ goodsId: id });

        // 重新加载当前页的数据
        this.loadFavoriteList();
      } catch (error) {
        console.error("取消收藏失败:", error);
      }
    },

    // 加入购物车
    async handleAddToCart(id) {
      try {
        // 找到对应的设备信息
        const goods = this.favoriteList.find((item) => item.id === id);
        if (!goods) {
          Message.error("设备信息获取失败");
          return;
        }

        // 构建设备数据
        const cartItem = {
          sku_id: goods.sku_id,
          name: goods.name,
          price: parseFloat(goods.price) || 0,
          quantity: 1,
          thumbnail_url: goods.thumbnail_url || "",
          supplier_name: goods.supplier_name || "未知供应商",
          supplier_id: goods.supplier_id || "",
          short_description: goods.short_description || "",
          goods_id: goods.id,
        };

        // 调用API添加设备到购物车
        const response = await api.goods.addToCart(cartItem);
        Message.success("成功加入购物车");
      } catch (error) {
        console.error("加入购物车失败:", error);
      }
    },

    // 立即租赁
    handleQuickRent(id) {
      // 跳转到租赁详情页
      this.$router.push({ name: "GoodsDetail", params: { id } });
    },

    // 跳转到租赁页面
    goToRentPage() {
      this.$router.push("/rent");
    },

    // 处理分页变化
    handlePageChange(page) {
      if (page < 1 || page > this.totalPages) {
        return;
      }

      this.page_current = page;
      this.loadFavoriteList();
    },

    // 切换排序下拉菜单
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },

    // 处理排序变化
    handleSortChange(sortBy) {
      this.sortBy = sortBy;
      this.page_current = 1; // 重置到第一页
      this.loadFavoriteList();
      this.showSortDropdown = false; // 关闭下拉菜单
    },

    // 处理文档点击，关闭下拉菜单
    handleDocumentClick(event) {
      const sortBtn = document.getElementById("sortBtn");
      const sortDropdown = document.getElementById("sortDropdown");

      // 如果点击的不是排序按钮或下拉菜单本身，则关闭下拉菜单
      if (
        sortBtn &&
        sortDropdown &&
        !sortBtn.contains(event.target) &&
        !sortDropdown.contains(event.target)
      ) {
        this.showSortDropdown = false;
      }
    },

    // 跳转到设备详情页
    goToDetail(goodsId) {
      this.$router.push({ name: "GoodsDetail", params: { id: goodsId } });
    },

    // 跳转到订单页面
    goToOrders() {
      Message.info("即将跳转到订单页面");
      // 实际项目中可以替换为真实的订单页面路由
      // this.$router.push('/ucenter/orders')
    },

    // 跳转到个人资料页面
    goToProfile() {
      Message.info("即将跳转到个人资料页面");
      // 实际项目中可以替换为真实的个人资料页面路由
      // this.$router.push('/ucenter/profile')
    },

    // 跳转到账号设置页面
    goToSettings() {
      Message.info("即将跳转到账号设置页面");
      // 实际项目中可以替换为真实的账号设置页面路由
      // this.$router.push('/ucenter/settings')
    },

    // 跳转到帮助中心
    goToHelp() {
      Message.info("即将跳转到帮助中心");
      // 实际项目中可以替换为真实的帮助中心页面路由
      // this.$router.push('/help')
    },

    // 格式化日期
    formatDate(dateString, format = "YYYY-MM-DD") {
      return formatDate(dateString, format);
    },

    // 格式化价格
    formatPrice(price, decimals = 2) {
      return formatPrice(price, decimals);
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/pages/ucenter/Favorites.vue